import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Trash2, Search, ChefHat, Layers3 } from "lucide-react";
import { productsAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useCurrency } from "@/hooks/useCurrency";
import { Price } from "@/components/ui/price";

export type StockProduct = {
	id: number;
	name: string;
	unit: string;
	type: string;
	purchasePrice: number;
	sellingPrice: number;
	isWeightBased?: boolean;
	variants?: VariantDraft[];
};
export type ComponentLine = {
	productId: number;
	quantity: number;
	unit: string;
	wastePercent: number;
	isOptional?: boolean;
	product?: StockProduct;
	variantId?: string;
};
export type OptionDraft = {
	id?: number;
	name: string;
	priceAdjustment: number;
	ingredientProductId?: number | null;
	extraQuantity?: number | null;
	extraQuantityUnit?: string | null;
	ingredientProduct?: StockProduct;
};
export type GroupDraft = {
	id?: number;
	name: string;
	isRequired: boolean;
	minSelections: number;
	maxSelections: number;
	options: OptionDraft[];
};
export type VariantDraft = {
	id?: string;
	name: string;
	sellingPrice: number;
	purchasePrice?: number;
	components: ComponentLine[];
};
export type ConfigurationDraft = {
	variants: VariantDraft[];
	ingredients: ComponentLine[];
	components: ComponentLine[];
	groups: GroupDraft[];
};
export const emptyConfiguration = (): ConfigurationDraft => ({
	ingredients: [],
	components: [],
	groups: [],
	variants: [],
});
export function componentCost(lines: ComponentLine[], recipe: boolean) {
	return lines.reduce(
		(sum, line) =>
			sum +
			Number(
				line.product?.variants?.find(v => v.id === line.variantId)?.purchasePrice ??
					line.product?.purchasePrice ??
					0,
			) *
				Number(line.quantity) *
				(recipe && ["gram", "milliliter"].includes(line.unit) ? 0.001 : 1) *
				(1 + Number(line.wastePercent || 0) / 100),
		0,
	);
}

function ProductSearch({
	onSelect,
	disabled,
	excludeId,
	ingredientsOnly = false,
	branchId,
}: {
	onSelect: (p: StockProduct) => void;
	disabled?: boolean;
	excludeId: number;
	ingredientsOnly?: boolean;
	branchId?: number;
}) {
	const { t } = useTranslation();
	const [query, setQuery] = useState("");
	const [results, setResults] = useState<StockProduct[]>([]);
	const [error, setError] = useState(false);
	const [loading, setLoading] = useState(false);
	useEffect(() => {
		let active = true;
		if (!query.trim()) {
			setResults([]);
			return;
		}
		setLoading(true);
		const timer = setTimeout(() => {
			setLoading(true);
			setError(false);
			productsAPI
				.getAll({ search: query, limit: 30, branchId })
				.then(data => {
					if (active)
						setResults(
							(data.products || []).filter(
								(p: StockProduct) => p.id !== excludeId && (!ingredientsOnly || p.type === "product"),
							),
						);
				})
				.catch(() => {
					if (active) setError(true);
				})
				.finally(() => {
					if (active) setLoading(false);
				});
		}, 200);
		return () => {
			active = false;
			clearTimeout(timer);
		};
	}, [query, excludeId, ingredientsOnly, branchId]);
	return (
		<div className="space-y-2">
			<div className="relative">
				<Search className="absolute start-3 top-3 h-4 w-4 text-muted-foreground" />
				<Input
					aria-label={t("productSetup.search")}
					placeholder={t("productSetup.search")}
					value={query}
					onChange={e => setQuery(e.target.value)}
					disabled={disabled}
					className="ps-9"
				/>
			</div>
			{query && (
				<div className="max-h-48 overflow-y-auto rounded-xl bg-muted/40 p-1" aria-live="polite">
					{loading ? (
						<p className="p-2 text-sm">{t("common.loading")}</p>
					) : error ? (
						<p className="p-2 text-sm text-destructive">{t("productSetup.loadError")}</p>
					) : !results.length ? (
						<p className="p-2 text-sm text-muted-foreground">{t("productSetup.noResults")}</p>
					) : (
						results.map(p => (
							<Button
								key={p.id}
								variant="ghost"
								className="w-full justify-between text-start"
								onClick={() => {
									onSelect(p);
									setQuery("");
								}}
							>
								<span className="truncate">{p.name}</span>
								<Plus className="h-4 w-4 shrink-0" />
							</Button>
						))
					)}
				</div>
			)}
		</div>
	);
}

export function ProductCompositionEditor({
	type,
	productId,
	branchId,
	value,
	onChange,
	basePrice,
	disabled,
	recipeDisabled = false,
	groupsDisabled = false,
	hideGroups = false,
}: {
	type: string;
	productId: number;
	branchId?: number;
	value: ConfigurationDraft;
	onChange: (value: ConfigurationDraft) => void;
	basePrice: number;
	disabled: boolean;
	recipeDisabled?: boolean;
	groupsDisabled?: boolean;
	hideGroups?: boolean;
}) {
	const { t } = useTranslation();
	const currency = useCurrency();
	const recipe = type === "menu_item";
	const key = recipe ? "ingredients" : "components";
	const lines = value[key];
	const patchLine = (i: number, patch: Partial<ComponentLine>) =>
		onChange({ ...value, [key]: lines.map((line, index) => (index === i ? { ...line, ...patch } : line)) });
	const patchGroup = (i: number, patch: Partial<GroupDraft>) =>
		onChange({ ...value, groups: value.groups.map((g, index) => (index === i ? { ...g, ...patch } : g)) });
	const patchOption = (g: number, o: number, patch: Partial<OptionDraft>) =>
		patchGroup(g, {
			options: value.groups[g].options.map((option, index) => (index === o ? { ...option, ...patch } : option)),
		});
	const unitOptions = (weighted: boolean) => (weighted ? ["gram", "kilogram", "milliliter", "liter"] : ["piece"]);
	const locked = disabled || (recipe && recipeDisabled);
	return (
		<div className="space-y-4" data-product-configuration>
			<section className="rounded-2xl bg-card p-4 shadow-sm space-y-3">
				<div className="flex flex-wrap items-center justify-between gap-2">
					<div className="flex items-center gap-2">
						{recipe ? (
							<ChefHat className="h-5 w-5 text-emerald-700 dark:text-emerald-300" />
						) : (
							<Layers3 className="h-5 w-5 text-violet-700 dark:text-violet-300" />
						)}
						<h2 className="font-semibold">
							{t(recipe ? "productSetup.ingredients" : "productSetup.components")}
						</h2>
					</div>
					<span className="text-sm text-muted-foreground">
						{t("productSetup.cost")}: <Price currency={currency} value={componentCost(lines, recipe)} />
					</span>
				</div>
				<p className="text-xs text-muted-foreground">
					{t(recipe ? "productSetup.ingredientsHint" : "productSetup.componentsHint")}
				</p>
				{recipeDisabled && (
					<p className="text-sm text-muted-foreground">{t("productSetup.recipePermission")}</p>
				)}
				{lines.map((line, index) => (
					<div
						key={`${line.productId}-${index}`}
						className="grid gap-3 rounded-xl bg-muted/35 p-3 lg:grid-cols-[minmax(0,1fr)_minmax(22rem,1.4fr)] lg:items-end"
					>
						<div className="flex items-center justify-between gap-2">
							<span className="font-medium text-sm">{line.product?.name || `#${line.productId}`}</span>
							<Button
								size="icon"
								variant="ghost"
								disabled={locked}
								aria-label={t("productSetup.removeItem", { name: line.product?.name })}
								onClick={() => onChange({ ...value, [key]: lines.filter((_, i) => i !== index) })}
							>
								<Trash2 className="h-4 w-4" />
							</Button>
						</div>
						{!recipe && !!line.product?.variants?.length && (
							<Select
								value={line.variantId || ""}
								onValueChange={variantId => patchLine(index, { variantId })}
								disabled={locked}
							>
								<SelectTrigger aria-label={t("productSetup.chooseVariant")}>
									<SelectValue placeholder={t("productSetup.chooseVariant")} />
								</SelectTrigger>
								<SelectContent>
									{line.product.variants.map(v => (
										<SelectItem key={v.id} value={v.id!}>
											{v.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						)}
						<div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
							<div className="space-y-1">
								<Label htmlFor={`component-qty-${index}`}>{t("productSetup.quantity")}</Label>
								<Input
									id={`component-qty-${index}`}
									type="number"
									min="0.0001"
									step="0.001"
									value={line.quantity}
									disabled={locked}
									onChange={e => patchLine(index, { quantity: Number(e.target.value) })}
								/>
							</div>
							{recipe ? (
								<>
									<div className="space-y-1">
										<Label>{t("products.form.unit")}</Label>
										<Select
											value={line.unit}
											onValueChange={unit => patchLine(index, { unit })}
											disabled={locked}
										>
											<SelectTrigger aria-label={t("products.form.unit")}>
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												{unitOptions(
													["weight", "bulk"].includes(line.product?.unit || "") ||
														!!line.product?.isWeightBased,
												).map(unit => (
													<SelectItem key={unit} value={unit}>
														{t(`productSetup.units.${unit}`)}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>
									<div className="space-y-1">
										<Label htmlFor={`component-waste-${index}`}>{t("productSetup.waste")}</Label>
										<Input
											id={`component-waste-${index}`}
											type="number"
											min="0"
											max="100"
											step="0.1"
											value={line.wastePercent}
											disabled={locked}
											onChange={e => patchLine(index, { wastePercent: Number(e.target.value) })}
										/>
									</div>
								</>
							) : (
								<div className="flex items-end pb-3 text-sm text-muted-foreground">
									{t(
										line.product?.unit === "weight"
											? "productSetup.units.kilogram"
											: "productSetup.units.piece",
									)}
								</div>
							)}
						</div>
					</div>
				))}
				{!locked && (
					<ProductSearch
						excludeId={productId}
						ingredientsOnly={recipe}
						branchId={branchId}
						onSelect={p => {
							const existing = lines.findIndex(l => l.productId === p.id);
							if (existing >= 0) {
								patchLine(existing, { quantity: Number(lines[existing].quantity) + 1 });
								return;
							}
							onChange({
								...value,
								[key]: [
									...lines,
									{
										productId: p.id,
										product: p,
										quantity: 1,
										unit:
											p.unit === "weight" || p.unit === "bulk" || p.isWeightBased
												? "gram"
												: "piece",
										wastePercent: 0,
									},
								],
							});
						}}
					/>
				)}
			</section>
			{!hideGroups && (
				<section className="rounded-2xl bg-card p-4 shadow-sm space-y-3">
					<h2 className="font-semibold">{t("productSetup.choices")}</h2>
					<p className="text-xs text-muted-foreground">{t("productSetup.choicesHint")}</p>
					<p className="rounded-xl bg-muted/40 p-3 text-xs text-muted-foreground">
						{t("productSetup.freeNotes")}
					</p>
					{value.groups.map((group, gi) => (
						<div key={group.id ?? `new-${gi}`} className="rounded-xl border p-3 space-y-3">
							<div className="flex gap-2">
								<Input
									aria-label={t("modifiers.groupName")}
									placeholder={t("productSetup.groupPlaceholder")}
									value={group.name}
									disabled={disabled || groupsDisabled}
									onChange={e => patchGroup(gi, { name: e.target.value })}
								/>
								<Button
									size="icon"
									variant="ghost"
									disabled={disabled || groupsDisabled}
									aria-label={t("productSetup.removeGroup")}
									onClick={() =>
										onChange({ ...value, groups: value.groups.filter((_, i) => i !== gi) })
									}
								>
									<Trash2 className="h-4 w-4" />
								</Button>
							</div>
							<details>
								<summary className="cursor-pointer py-2 text-xs text-muted-foreground">
									{t("productSetup.selectionRules")}
								</summary>
								<div className="grid grid-cols-3 gap-3 items-end">
									<div className="flex flex-wrap items-center gap-2 pb-3">
										<Switch
											id={`group-required-${gi}`}
											checked={group.isRequired}
											disabled={disabled || groupsDisabled}
											onCheckedChange={isRequired =>
												patchGroup(gi, {
													isRequired,
													minSelections: isRequired ? Math.max(1, group.minSelections) : 0,
												})
											}
										/>
										<Label htmlFor={`group-required-${gi}`}>{t("modifiers.required")}</Label>
									</div>
									<div>
										<Label htmlFor={`group-min-${gi}`}>{t("productSetup.minimum")}</Label>
										<Input
											id={`group-min-${gi}`}
											type="number"
											min={group.isRequired ? 1 : 0}
											value={group.minSelections}
											disabled={disabled || groupsDisabled}
											onChange={e => patchGroup(gi, { minSelections: Number(e.target.value) })}
										/>
									</div>
									<div>
										<Label htmlFor={`group-max-${gi}`}>{t("productSetup.maximum")}</Label>
										<Input
											id={`group-max-${gi}`}
											type="number"
											min="1"
											value={group.maxSelections}
											disabled={disabled || groupsDisabled}
											onChange={e => patchGroup(gi, { maxSelections: Number(e.target.value) })}
										/>
									</div>
								</div>
							</details>
							{group.options.map((option, oi) => (
								<div key={option.id ?? `new-${oi}`} className="bg-muted/35 rounded-xl p-3 space-y-2">
									<div className="grid grid-cols-[minmax(0,1fr)_7rem_2.75rem] gap-2 items-end">
										<div>
											<Label htmlFor={`option-name-${gi}-${oi}`}>
												{t("modifiers.optionName")}
											</Label>
											<Input
												id={`option-name-${gi}-${oi}`}
												value={option.name}
												disabled={disabled || groupsDisabled}
												onChange={e => patchOption(gi, oi, { name: e.target.value })}
											/>
										</div>
										<div>
											<Label htmlFor={`option-price-${gi}-${oi}`}>
												{t("productSetup.priceExtra")}
											</Label>
											<Input
												id={`option-price-${gi}-${oi}`}
												type="number"
												step="0.01"
												value={option.priceAdjustment}
												disabled={disabled || groupsDisabled}
												onChange={e =>
													patchOption(gi, oi, { priceAdjustment: Number(e.target.value) })
												}
											/>
										</div>
										<Button
											size="icon"
											variant="ghost"
											disabled={disabled || groupsDisabled}
											aria-label={t("productSetup.removeOption")}
											onClick={() =>
												patchGroup(gi, { options: group.options.filter((_, i) => i !== oi) })
											}
										>
											<Trash2 className="h-4 w-4" />
										</Button>
									</div>
									<p className="text-xs text-muted-foreground">
										{Number(option.priceAdjustment) === 0 ? `${t("productSetup.free")} · ` : ""}
										{t("productSetup.optionTotal")}:{" "}
										<Price
											currency={currency}
											value={Number(basePrice) + Number(option.priceAdjustment)}
										/>
									</p>
									{option.ingredientProductId ? (
										<div className="grid grid-cols-2 gap-2">
											<div className="col-span-2 flex items-center justify-between text-sm">
												<span>
													{option.ingredientProduct?.name || `#${option.ingredientProductId}`}
												</span>
												<Button
													size="sm"
													variant="ghost"
													disabled={disabled || groupsDisabled}
													onClick={() =>
														patchOption(gi, oi, {
															ingredientProductId: null,
															extraQuantity: null,
															extraQuantityUnit: null,
															ingredientProduct: undefined,
														})
													}
												>
													{t("common.remove")}
												</Button>
											</div>
											<Input
												aria-label={t("productSetup.extraQuantity")}
												type="number"
												min="0.0001"
												step="0.001"
												value={option.extraQuantity ?? 1}
												disabled={disabled || groupsDisabled}
												onChange={e =>
													patchOption(gi, oi, { extraQuantity: Number(e.target.value) })
												}
											/>
											<Select
												value={option.extraQuantityUnit || "piece"}
												disabled={disabled || groupsDisabled}
												onValueChange={extraQuantityUnit =>
													patchOption(gi, oi, { extraQuantityUnit })
												}
											>
												<SelectTrigger aria-label={t("products.form.unit")}>
													<SelectValue />
												</SelectTrigger>
												<SelectContent>
													{unitOptions(
														["weight", "bulk"].includes(
															option.ingredientProduct?.unit || "",
														),
													).map(unit => (
														<SelectItem key={unit} value={unit}>
															{t(`productSetup.units.${unit}`)}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</div>
									) : (
										!disabled &&
										!groupsDisabled && (
											<details>
												<summary className="text-xs text-muted-foreground cursor-pointer py-2">
													{t("productSetup.extraIngredient")}
												</summary>
												<ProductSearch
													excludeId={productId}
													ingredientsOnly
													branchId={branchId}
													onSelect={p =>
														patchOption(gi, oi, {
															ingredientProductId: p.id,
															ingredientProduct: p,
															extraQuantity: 1,
															extraQuantityUnit: p.unit === "weight" ? "gram" : "piece",
														})
													}
												/>
											</details>
										)
									)}
								</div>
							))}
							{!disabled && !groupsDisabled && (
								<Button
									variant="outline"
									size="sm"
									onClick={() =>
										patchGroup(gi, {
											options: [...group.options, { name: "", priceAdjustment: 0 }],
											maxSelections: group.isRequired
												? group.maxSelections
												: Math.max(group.maxSelections, group.options.length + 1),
										})
									}
								>
									<Plus className="h-4 w-4 me-2" />
									{t("modifiers.addOption")}
								</Button>
							)}
						</div>
					))}
					{!disabled && !groupsDisabled && (
						<Button
							variant="outline"
							onClick={() =>
								onChange({
									...value,
									groups: [
										...value.groups,
										{
											name: "",
											isRequired: false,
											minSelections: 0,
											maxSelections: 1,
											options: [{ name: "", priceAdjustment: 0 }],
										},
									],
								})
							}
						>
							<Plus className="h-4 w-4 me-2" />
							{t("modifiers.addGroup")}
						</Button>
					)}
				</section>
			)}
		</div>
	);
}

export function ProductSetupEditor(props: Parameters<typeof ProductCompositionEditor>[0]) {
	const { t } = useTranslation();
	const [selected, setSelected] = useState(0);
	const { value, onChange } = props;
	const variants = value.variants || [];
	const index = Math.min(selected, Math.max(0, variants.length - 1));
	const variant = variants[index];
	const recipe = props.type === "menu_item";
	const key = recipe ? "ingredients" : "components";
	const updateVariant = (patch: Partial<VariantDraft>) =>
		onChange({ ...value, variants: variants.map((v, i) => (i === index ? { ...v, ...patch } : v)) });
	return (
		<div className="space-y-4">
			<section className="rounded-2xl bg-card p-4 space-y-3 shadow-sm">
				<div className="flex flex-wrap justify-between gap-2">
					<div>
						<h2 className="font-semibold">{t("productSetup.variants")}</h2>
						<p className="text-xs text-muted-foreground mt-1">{t("productSetup.variantHint")}</p>
					</div>
					{!props.disabled && !(props.type === "menu_item" && props.recipeDisabled) && (
						<Button
							variant="outline"
							onClick={() => {
								onChange({
									...value,
									variants: [
										...variants,
										{
											name: "",
											sellingPrice: props.basePrice,
											components:
												variant?.components.map(c => ({ ...c })) ||
												value[key].map(c => ({ ...c })),
										},
									],
								});
								setSelected(variants.length);
							}}
						>
							<Plus className="h-4 w-4 me-2" />
							{t("productSetup.addVariant")}
						</Button>
					)}
				</div>
				{variants.length > 0 && (
					<>
						<div className="flex flex-wrap gap-2" role="group" aria-label={t("productSetup.variants")}>
							{variants.map((v, i) => (
								<Button
									aria-pressed={index === i}
									key={v.id || i}
									variant={index === i ? "default" : "outline"}
									onClick={() => setSelected(i)}
								>
									{v.name || `${t("productSetup.variants")} ${i + 1}`}
								</Button>
							))}
						</div>
						<div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_2.75rem] items-end gap-3">
							<div>
								<Label htmlFor="variant-name">{t("productSetup.variantName")}</Label>
								<Input
									id="variant-name"
									value={variant.name}
									disabled={props.disabled || (props.type === "menu_item" && props.recipeDisabled)}
									onChange={e => updateVariant({ name: e.target.value })}
								/>
							</div>
							<div>
								<Label htmlFor="variant-price">{t("productSetup.variantPrice")}</Label>
								<Input
									id="variant-price"
									type="number"
									min="0"
									step="0.01"
									value={variant.sellingPrice}
									disabled={props.disabled || (props.type === "menu_item" && props.recipeDisabled)}
									onChange={e => updateVariant({ sellingPrice: Number(e.target.value) })}
								/>
							</div>
							<Button
								size="icon"
								variant="ghost"
								disabled={props.disabled || (props.type === "menu_item" && props.recipeDisabled)}
								aria-label={t("productSetup.removeVariant")}
								onClick={() => {
									onChange({
										...value,
										...(variants.length === 1 ? { [key]: variant.components } : {}),
										variants: variants.filter((_, i) => i !== index),
									});
									setSelected(0);
								}}
							>
								<Trash2 className="h-4 w-4" />
							</Button>
						</div>
					</>
				)}
			</section>
			<ProductCompositionEditor
				{...props}
				value={variant ? { ...value, [key]: variant.components } : value}
				onChange={next =>
					variant
						? onChange({
								...value,
								groups: next.groups,
								variants: variants.map((v, i) => (i === index ? { ...v, components: next[key] } : v)),
							})
						: onChange(next)
				}
				basePrice={variant?.sellingPrice ?? props.basePrice}
			/>
		</div>
	);
}
